import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Guides.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Guides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const response = await fetch(`${API_URL}/guides`);
        if (!response.ok) throw new Error('Failed to fetch guides');
        const data = await response.json();
        setGuides(data.guides || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, []);

  const getCategoryLabel = (value) => {
    const categories = {
      'invoicing': 'Invoicing Basics & Best Practices',
      'bill-of-sale': 'Bill of Sale Articles',
      'online-safety': 'Online Safety & Scam Prevention',
      'buying-selling': 'Buying and Selling Tips',
      'business': 'General Business Advice',
      'thrifting': 'Thrifting to Marketplace Pipeline'
    };
    return categories[value] || value;
  };

  if (loading) {
    return (
      <div className="guides-page">
        <div className="guides-loading container">
          <div className="loading-spinner"></div>
          <p>Loading guides...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guides-page">
        <div className="guides-error container">
          <h1>Error Loading Guides</h1>
          <p>{error}</p>
          <Link to="/blog" className="btn-primary">Browse Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="guides-page">
      <Helmet>
        <title>Premium Guides | PrivateSold</title>
        <meta name="description" content="In-depth guides for buying and selling items safely. Comprehensive resources covering bills of sale, invoices, and transaction safety." />
        <link rel="canonical" href="https://privatesold.com/guides" />
      </Helmet>

      <section className="guides-hero">
        <div className="container">
          <h1>Premium Guides</h1>
          <p>In-depth resources to help you buy and sell safely</p>
        </div>
      </section>

      <section className="guides-list container">
        {guides.length === 0 ? (
          <div className="no-guides">
            <h2>Coming Soon</h2>
            <p>We're working on comprehensive guides to help you with your private sales.</p>
            <Link to="/blog" className="btn-primary">Read Our Free Articles</Link>
          </div>
        ) : (
          <div className="guides-grid">
            {guides.map(guide => (
              <div key={guide._id} className="guide-card">
                {guide.featuredImage?.url && (
                  <div className="guide-image">
                    <img src={guide.featuredImage.url} alt={guide.premiumTitle || guide.title} />
                  </div>
                )}
                <div className="guide-content">
                  <span className="guide-category">{getCategoryLabel(guide.category)}</span>
                  <h2>{guide.premiumTitle || guide.title}</h2>
                  <p className="guide-description">
                    {guide.premiumDescription || guide.excerpt}
                  </p>
                  
                  {guide.premiumPreviewPoints?.length > 0 && (
                    <ul className="guide-preview-points">
                      {guide.premiumPreviewPoints.slice(0, 4).map((point, idx) => (
                        <li key={idx}>✓ {point}</li>
                      ))}
                    </ul>
                  )}
                  
                  <div className="guide-footer">
                    <span className="guide-price">${(guide.premiumPrice || 7.99).toFixed(2)}</span>
                    <Link to={`/blog/${guide.slug}`} className="btn-primary">
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="guides-faq container">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>What format are the guides?</h3>
            <p>All guides are delivered as downloadable PDF files that you can save and reference anytime.</p>
          </div>
          <div className="faq-item">
            <h3>How do I access my purchase?</h3>
            <p>After purchase, you'll receive an email with a download link. You can also re-download from your account.</p>
          </div>
          <div className="faq-item">
            <h3>Is there a refund policy?</h3>
            <p>Due to the digital nature of our guides, we offer refunds within 7 days if you're not satisfied.</p>
          </div>
          <div className="faq-item">
            <h3>Can I share the guide?</h3>
            <p>Guides are for personal use only. Each purchase is licensed to a single user.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Guides;
